import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { FaCopy, FaCheckCircle, FaShareAlt, FaUsers, FaLink, FaChartPie, FaLightbulb } from "react-icons/fa";
import { useAppContext } from "../context/AppContext";

/* ── Theme ── */
const T = {
  bg: "#050d1a",
  bgCard: "#0a1628",
  bgCard2: "#0d1f38",
  border: "#1a2d4a",
  accent: "#eab308",
  accentDim: "rgba(234,179,8,0.1)",
  accentGlow: "rgba(234,179,8,0.3)",
  blue: "#3b82f6",
  blueDim: "rgba(59,130,246,0.1)",
  textPrimary: "#f0f6ff",
  textSecondary: "#94a3b8",
  textMuted: "#4b6280",
  success: "#22c55e",
  successDim: "rgba(34,197,94,0.1)",
};

export default function ReferralSection() {
  const { FetchUserData, HandleFetchUserData } = useAppContext();
  const [copiedKey, setCopiedKey] = useState("");

  useEffect(() => {
    HandleFetchUserData?.();
  }, [HandleFetchUserData]);

  const referralCode = FetchUserData?.referralCode || "—";

  const referralLink = useMemo(() => {
    const origin = typeof window !== "undefined" && window.location?.origin ? window.location.origin : "";
    return referralCode && referralCode !== "—"
      ? `${origin}/login?ref=${encodeURIComponent(referralCode)}`
      : `${origin}/login`;
  }, [referralCode]);

  const tiers = [
    { level: 1, pct: "8%" }, { level: 2, pct: "5%" }, { level: 3, pct: "4%" },
    { level: 4, pct: "3%" }, { level: 5, pct: "2%" }, { level: 6, pct: "1%" },
    { level: 7, pct: "1%" }, { level: 8, pct: "1%" }, { level: 9, pct: "0.5%" },
    { level: 10, pct: "0.5%" },
  ];

  const copyText = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(""), 2000);
    } catch (e) {
      console.error("Copy failed");
    }
  };

  const referrals = Array.isArray(FetchUserData?.Referal) ? FetchUserData.Referal : [];

  return (
    <div style={{
      width: "100%", background: "transparent",
      color: T.textPrimary, fontFamily: "system-ui, -apple-system, sans-serif",
    }}>
      <div style={{
        maxWidth: 1280, margin: "0 auto",
        padding: "20px clamp(12px, 4vw, 24px) 40px",
        display: "flex", flexDirection: "column", gap: 36,
      }}>

        {/* ══════════ HEADER ══════════ */}
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          style={{ display: "flex", flexDirection: "column", gap: 12 }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ height: 3, width: 44, background: `linear-gradient(90deg,${T.accent},transparent)`, borderRadius: 999 }} />
            <span style={{ color: T.accent, fontWeight: 900, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.3em" }}>
              Network Growth
            </span>
          </div>
          <h1 style={{
            fontSize: "clamp(1.8rem, 5vw, 2.6rem)",
            fontWeight: 900, color: T.textPrimary,
            letterSpacing: "-0.03em", margin: 0, textTransform: "uppercase",
          }}>
            Referral <span style={{ color: T.accent }}>Terminal</span>
          </h1>
          <p style={{ fontSize: "clamp(0.85rem, 2vw, 13px)", color: T.textSecondary, maxWidth: 600, lineHeight: 1.7, fontWeight: 500 }}>
            Expand your 3Workers network and unlock passive revenue across 10 strategic tiers.
            Real-time tracking enabled for all direct and indirect nodes.
          </p>
        </motion.div>

        {/* ══════════ PRIMARY GRID ══════════ */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
          gap: 24,
        }}>

          {/* ── Referral Code Card — from left ── */}
          <motion.div
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -4 }}
            style={{
              background: T.bgCard,
              border: `1px solid ${T.border}`,
              borderRadius: 28, 
              padding: "clamp(20px, 4vw, 28px) clamp(16px, 4vw, 26px)",
              position: "relative", overflow: "hidden",
              boxShadow: "0 16px 48px rgba(0,0,0,0.4)",
            }}
          >
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, height: 3,
              background: `linear-gradient(90deg,${T.blue},transparent)`,
            }} />
            <div style={{
              position: "absolute", top: -40, right: -40,
              width: 140, height: 140, borderRadius: "50%",
              background: "radial-gradient(ellipse,rgba(59,130,246,0.1) 0%,transparent 70%)",
              pointerEvents: "none",
            }} />

            <div style={{
              position: "relative", zIndex: 1,
              display: "flex", flexWrap: "wrap", alignItems: "center",
              justifyContent: "space-between", gap: 20, marginBottom: 28,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <motion.div
                  initial={{ scale: 0, rotate: -20 }}
                  whileInView={{ scale: 1, rotate: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 260, damping: 16 }}
                  style={{
                    width: 58, height: 58, borderRadius: 20,
                    background: `linear-gradient(135deg,#1e40af,${T.blue})`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 10px 26px rgba(59,130,246,0.35)",
                    flexShrink: 0,
                  }}
                >
                  <FaUsers size={24} color="#fff" />
                </motion.div>
                <div>
                  <p style={{ fontSize: 9, fontWeight: 900, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.2em", margin: "0 0 4px" }}>
                    Active Code
                  </p>
                  <p style={{ fontSize: "clamp(1.2rem, 4vw, 1.5rem)", fontWeight: 900, color: T.textPrimary, letterSpacing: "0.05em", margin: 0 }}>
                    {referralCode}
                  </p>
                </div>
              </div>

              <motion.button
                whileTap={{ scale: 0.95 }}
                whileHover={{ scale: 1.03 }}
                onClick={() => referralCode !== "—" && copyText(referralCode, "code")}
                style={{
                  padding: "13px 26px", borderRadius: 16,
                  fontWeight: 900, fontSize: 10, textTransform: "uppercase",
                  letterSpacing: "0.15em", border: "none", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  background: copiedKey === "code" ? T.success : T.accent,
                  color: copiedKey === "code" ? "#fff" : "#0a0d14",
                  boxShadow: copiedKey === "code"
                    ? "0 8px 24px rgba(34,197,94,0.35)"
                    : "0 8px 24px rgba(234,179,8,0.35)",
                  transition: "background 0.2s, box-shadow 0.2s",
                  flexGrow: window.innerWidth < 400 ? 1 : 0,
                }}
              >
                {copiedKey === "code" ? <FaCheckCircle size={14} /> : <FaCopy size={12} />}
                {copiedKey === "code" ? "Verified" : "Copy Code"}
              </motion.button>
            </div>

            {/* invite link */}
            <div style={{
              position: "relative", zIndex: 1,
              paddingTop: 24, borderTop: `1px solid ${T.border}`,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <FaLink style={{ color: T.blue, fontSize: 11 }} />
                <p style={{ fontSize: 9, fontWeight: 900, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.2em", margin: 0 }}>
                  Universal Invite Link
                </p>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{
                  flex: 1, background: "rgba(0,0,0,0.3)",
                  border: `1px solid ${T.border}`,
                  borderRadius: 16, padding: "14px 18px",
                  fontSize: "clamp(10px, 2.5vw, 11px)", fontWeight: 700, color: T.textSecondary,
                  wordBreak: "break-all",
                }}>
                  {referralLink}
                </div>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => copyText(referralLink, "link")}
                  style={{
                    padding: "13px 20px", borderRadius: 16,
                    fontWeight: 900, fontSize: 10, textTransform: "uppercase",
                    letterSpacing: "0.15em", cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    background: copiedKey === "link" ? T.success : T.blueDim,
                    color: copiedKey === "link" ? "#fff" : T.blue,
                    border: copiedKey === "link" ? "none" : `1px solid rgba(59,130,246,0.3)`,
                    transition: "all 0.2s",
                  }}
                >
                  <FaShareAlt size={12} /> {copiedKey === "link" ? "Copied" : "Share"}
                </motion.button>
              </div>
            </div>
          </motion.div>

          {/* ── Team Insights Card — from right ── */}
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
            style={{
              background: T.bgCard,
              border: `1px solid ${T.border}`,
              borderRadius: 28, 
              padding: "clamp(20px, 4vw, 28px) clamp(16px, 4vw, 26px)",
              position: "relative", overflow: "hidden",
              boxShadow: "0 16px 48px rgba(0,0,0,0.4)",
            }}
          >
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, height: 3,
              background: `linear-gradient(90deg,${T.accent},transparent)`,
            }} />

            <div style={{
              position: "relative", zIndex: 1,
              display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12
            }}>
              <div>
                <p style={{ fontSize: 9, fontWeight: 900, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.2em", margin: "0 0 4px" }}>
                  Network Stats
                </p>
                <h2 style={{ fontSize: "clamp(1.1rem, 4vw, 1.3rem)", fontWeight: 900, color: T.textPrimary, margin: 0 }}>
                  Direct Nodes
                </h2>
              </div>
              <div style={{
                padding: "7px 18px", borderRadius: 999,
                background: T.accentDim,
                border: `1px solid rgba(234,179,8,0.25)`,
                color: T.accent, fontSize: 12, fontWeight: 900,
                whiteSpace: "nowrap"
              }}>
                {referrals.length} Members
              </div>
            </div>

            <div style={{
              display: "flex", flexDirection: "column", gap: 10,
              maxHeight: 280, overflowY: "auto", paddingRight: 4,
              position: "relative", zIndex: 1,
            }}>
              {referrals.length > 0 ? (
                referrals.slice().reverse().map((r, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: Math.min(idx, 6) * 0.05 }}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10,
                      background: "rgba(255,255,255,0.02)",
                      border: `1px solid ${T.border}`,
                      padding: "13px 16px", borderRadius: 16,
                      transition: "all 0.2s", cursor: "default",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = "rgba(234,179,8,0.04)"; e.currentTarget.style.borderColor = "rgba(234,179,8,0.25)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.02)"; e.currentTarget.style.borderColor = T.border; }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                      <div style={{
                        width: 38, height: 38, borderRadius: 12,
                        background: "rgba(255,255,255,0.04)",
                        border: `1px solid ${T.border}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: T.textMuted, flexShrink: 0,
                      }}>
                        <FaUsers size={14} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 900, color: T.textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {r?.userId?.name || "Member Node"}
                        </div>
                        <div style={{ fontSize: 9, color: T.textMuted, fontWeight: 700, textTransform: "uppercase", marginTop: 2 }}>
                          UID: {r?.userId?._id?.slice(-6).toUpperCase()}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                      <span style={{
                        padding: "4px 10px", borderRadius: 8,
                        background: T.blueDim,
                        border: `1px solid rgba(59,130,246,0.2)`,
                        fontSize: 9, fontWeight: 900, color: T.blue,
                      }}>
                        LVL {r?.userId?.level || 1}
                      </span>
                      <motion.div
                        animate={{ opacity: [1, 0.3, 1] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        style={{ width: 7, height: 7, borderRadius: "50%", background: T.success }}
                      />
                    </div>
                  </motion.div>
                ))
              ) : (
                <div style={{
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  padding: "44px 0", opacity: 0.4,
                }}>
                  <FaChartPie style={{ fontSize: 36, marginBottom: 12, color: T.textMuted }} />
                  <p style={{ fontSize: 12, fontWeight: 900, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.05em", textAlign: "center" }}>
                    No Active Nodes Found
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* ══════════ COMMISSION STRUCTURE ══════════ */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          style={{
            background: T.bgCard,
            border: `1px solid rgba(234,179,8,0.18)`,
            borderRadius: 32, padding: "clamp(24px, 4vw, 48px) clamp(20px, 4vw, 48px)",
            position: "relative", overflow: "hidden",
            boxShadow: "0 24px 70px rgba(0,0,0,0.45)",
          }}
        >
          {/* top accent */}
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 3,
            background: `linear-gradient(90deg,${T.accent},${T.blue},transparent)`,
          }} />

          <div style={{
            display: "flex", flexWrap: "wrap",
            alignItems: "flex-end", justifyContent: "space-between",
            gap: 20, marginBottom: 44, position: "relative", zIndex: 1,
          }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <FaChartPie style={{ color: T.accent, fontSize: 11 }} />
                <span style={{ color: T.accent, fontWeight: 900, fontSize: 9, textTransform: "uppercase", letterSpacing: "0.2em" }}>
                  Earning Protocol
                </span>
              </div>
              <h2 style={{ fontSize: "clamp(1.4rem, 4vw, 1.7rem)", fontWeight: 900, color: T.textPrimary, margin: "0 0 6px", textTransform: "uppercase", letterSpacing: "-0.02em" }}>
                Yield Structure
              </h2>
              <p style={{ color: T.textSecondary, fontSize: 12, fontWeight: 500, margin: 0 }}>
                Multi-tier commission distribution across 10 depth levels.
              </p>
            </div>

            <div style={{
              display: "flex", alignItems: "center", gap: 14,
              padding: "14px 18px", borderRadius: 20,
              background: T.accentDim,
              border: `1px solid rgba(234,179,8,0.25)`,
              flexGrow: window.innerWidth < 600 ? 1 : 0,
            }}>
              <motion.div
                animate={{ opacity: [1, 0.4, 1] }}
                transition={{ duration: 1.8, repeat: Infinity }}
                style={{ color: T.accent, fontSize: 16, flexShrink: 0 }}
              >
                <FaLightbulb />
              </motion.div>
              <p style={{ fontSize: 11, fontWeight: 700, color: "#fde68a", lineHeight: 1.4, margin: 0 }}>
                Tip: Higher tiers unlock <br /> exponential rewards.
              </p>
            </div>
          </div>

          {/* tier grid */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 100px), 1fr))",
            gap: 14, position: "relative", zIndex: 1,
          }}>
            {tiers.map((t, i) => (
              <motion.div
                key={t.level}
                initial={{ opacity: 0, scale: 0.85, y: 16 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04, duration: 0.5, type: "spring", stiffness: 240 }}
                whileHover={{ y: -6, scale: 1.04 }}
                style={{
                  background: "rgba(255,255,255,0.02)",
                  border: `1px solid ${T.border}`,
                  padding: "20px 14px", borderRadius: 20,
                  display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center",
                  cursor: "default", transition: "border-color 0.2s, background 0.2s",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(234,179,8,0.35)"; e.currentTarget.style.background = "rgba(234,179,8,0.04)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = "rgba(255,255,255,0.02)"; }}
              >
                <span style={{ fontSize: 8, fontWeight: 900, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.18em", marginBottom: 4 }}>
                  Level
                </span>
                <span style={{ fontSize: 18, fontWeight: 900, color: T.textPrimary, marginBottom: 10 }}>
                  {t.level}
                </span>
                <div style={{ width: "100%", height: 1, background: T.border, marginBottom: 10 }} />
                <span style={{ fontSize: 20, fontWeight: 900, color: T.accent, letterSpacing: "-0.02em" }}>
                  {t.pct}
                </span>
              </motion.div>
            ))}
          </div>

          {/* growth strategy note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.6 }}
            style={{
              marginTop: 36, padding: "clamp(16px, 4vw, 22px) clamp(16px, 4vw, 24px)", borderRadius: 22,
              background: "#030810",
              border: `1px solid ${T.border}`,
              color: T.textSecondary, fontSize: 12, fontWeight: 500,
              display: "flex", alignItems: "flex-start", flexWrap: "wrap", gap: 16,
              position: "relative", zIndex: 1,
            }}
          >
            <div style={{
              width: 42, height: 42, borderRadius: 14, flexShrink: 0,
              background: `linear-gradient(135deg,#1e40af,${T.blue})`,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff",
            }}>
              <FaUsers size={16} />
            </div>
            <div style={{ flex: "1 1 200px" }}>
              <span style={{ color: T.textPrimary, fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: 6, fontSize: 11 }}>
                Growth Strategy:
              </span>
              Share your referral terminal across encrypted channels (Telegram/WhatsApp) to maintain
              high node-conversion rates. More active nodes result in higher protocol rank.
            </div>
          </motion.div>
        </motion.section>
      </div>
    </div>
  );
}